import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bot } from 'lucide-react';
import type { RunDetail, RunMessageOutcome, RunSummary } from '@shared/types/factory';
import type { T3ActivityResult } from '@shared/agent-protocol';
import { EmptyState } from '@/components/EmptyState';
import { StatusBadge } from '@/components/StatusBadge';
import { AgentActivityFeed } from '@/components/factory/AgentActivityFeed';
import { AgentMessageBox } from '@/components/factory/AgentMessageBox';
import { RUN_THREAD_WORKING_META, isRunActive, liveRunMeta, runRef, runStatusMeta } from '@/components/factory/run-status';
import { agentReadOnlyReason, agentTabRun, folderGoneBoxNote, messageOutcomeText, queueBlockedReason } from '@/components/factory/live-ui';
import { RunFolderGoneCard } from '@/components/factory/FolderGoneCard';
import { runPath } from '@/navigation/project-paths';
import { useFollowScroll, useScrollEnd } from '@/hooks/use-follow-scroll';
import { JumpToLatestButton } from '@/components/JumpToLatestButton';
import { WorkingPill } from '@/components/factory/ChatMessageParts';
import type { ThreadWorking } from '@/components/factory/agent-activity';
import { useIsMobile } from '@/hooks/use-mobile';
import { QueuedMessages, type QueuedMessagesApi } from '@/components/factory/QueuedMessages';
import { cn } from '@/lib/utils';
import { stopRunTurn, summarizeRunThread, type restoreRunWorktree, type sendRunMessage } from '@/lib/runs-api';
import { toast } from 'sonner';
import { describeApiError } from '@/lib/api-errors';
import type { DiffReader } from '@/components/factory/ChangedFilesButton';

/**
 * SF-T-209: the task's Agent tab: the T3 thread of the task's newest run that has one, read live from its machine
 * as the conversation it is (AgentActivityFeed's thread layout), and a box to Steer or Queue messages for the agent
 * (Members and up). It works for any run with a thread, live or not. SF-T-222: in the side pane it follows the
 * thread as it grows until you scroll up (Jump to latest, or scrolling back to the bottom, follows again); Members can
 * ask for a summary of where the agent is. The feed's header (the changed files, Summarize) stays pinned as the thread
 * scrolls, and on phones so does the box.
 */
export function TaskAgentPane({
  taskId,
  runs,
  projectSlug,
  canMessage,
  fill = false,
  pinned = false,
  onSent,
  preview,
  send,
  summarize = summarizeRunThread,
  readDiff,
  queueApi,
  stop = stopRunTurn,
  runLine = true,
  onThreadWorking,
  restore,
  restoreDetail,
}: {
  taskId: string;
  runs: readonly RunSummary[] | undefined;
  projectSlug: string;
  /** Members and up may write to the agent. */
  canMessage: boolean;
  /** In the task's side pane: fills it, the thread scrolling above the box. */
  fill?: boolean;
  /** Phones (the task pop-up scrolls as one): the box pinned to the foot of the screen, with Jump to latest above it. */
  pinned?: boolean;
  onSent?: (outcome: RunMessageOutcome) => void;
  /** Preview only (the /dev/ui gallery). */
  preview?: T3ActivityResult;
  send?: typeof sendRunMessage;
  /** Preview only: stands in for the AI call. */
  summarize?: typeof summarizeRunThread;
  /** Preview only: stands in for the machine's diffs. */
  readDiff?: DiffReader | null;
  /** Preview only: stands in for the run's queue. */
  queueApi?: QueuedMessagesApi;
  /** Preview only: stands in for Stop. */
  stop?: typeof stopRunTurn;
  /** SF-T-266: false when the run line (AgentRunLine) is already the title above (the full-screen chat). */
  runLine?: boolean;
  /** SF-T-266: the run whose thread shows a turn going now, or null: for a run line shown above (its threadWorking). */
  onThreadWorking?: (runId: string | null) => void;
  /** Preview only: stands in for Continue in a new worktree (SF-T-273). */
  restore?: typeof restoreRunWorktree;
  /** Preview only: the restoring run's detail for its progress (SF-T-273). */
  restoreDetail?: RunDetail | null;
}) {
  const run = agentTabRun(runs);
  const stream = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const runId = run?.id;
  // The last read of the thread's error code, for the run it was read for.
  const [readError, setReadError] = useState<{ runId: string; code: string | null } | null>(null);
  // The newest of the thread in view, as a chat keeps it, until you scroll up; another run's thread starts at its newest.
  const { following, follow, anchor, spacer } = useFollowScroll(stream, content, fill && !!runId, runId);
  // Phones: the thread scrolls with the task round it, so only a jump to its end, not following.
  const section = useRef<HTMLElement>(null);
  const { away, jump } = useScrollEnd(section, pinned && !!runId, runId);
  // SF-T-266: each message sent reads the queue again at once.
  const [sentCount, setSentCount] = useState(0);
  // SF-T-266: on a phone, full screen (fill), the turn going now is a pill over the foot of the thread, as T3 Code's app.
  const isMobile = useIsMobile();
  const pill = fill && isMobile;
  const [turnSeen, setTurnSeen] = useState<{ runId: string; at: ThreadWorking } | null>(null);
  const turnNow = turnSeen && turnSeen.runId === runId ? turnSeen.at : null;
  const [queuedCount, setQueuedCount] = useState(0);
  // SF-T-273: the thread is stuck on its missing worktree folder (the feed's last read), for the box to point at the card.
  const [goneFor, setGoneFor] = useState<string | null>(null);

  const stopTurn = async (id: string, number: number) => {
    const res = await stop(id, { project: projectSlug });
    if (!res.data) toast.error(describeApiError(res, `Couldn't stop R-${number}'s agent`));
    else toast.success(`Stopping R-${number}'s agent: the run waits for your next message`);
  };

  if (!run) {
    return (
      <EmptyState
        size="compact"
        icon={<Bot className="size-4" />}
        title="No agent thread yet"
        description="The task's first run starts one. Run it live to work with the agent here, turn by turn."
      />
    );
  }
  const active = isRunActive(run.status);
  const readOnly =
    agentReadOnlyReason(run, readError?.runId === run.id ? readError.code : null) ?? (goneFor === run.id ? folderGoneBoxNote(run) : null);
  // SF-T-266: on a phone while the agent works, the queue is the Working pill's "N queued" (its panel), not above the box.
  const queueInPill = pill && !!turnNow;
  // SF-T-266: the agent's at work when the run says so, or when its thread shows a turn going though the run waits
  // (someone wrote to it in T3 Code): then the box queues by default, with Steer and Stop, never an ambiguous Send.
  const agentWorking = run.agent_working || (!!turnNow && (run.status === 'running' || (run.status === 'waiting' && !!run.live_parked_at)));
  const queueProps = canMessage && run.live && active;
  const queued =
    queueProps ? (
      <QueuedMessages
        variant={queueInPill ? 'count' : 'auto'}
        key={run.id}
        runId={run.id}
        project={projectSlug}
        polling={!run.live_parked_at || agentWorking}
        canSteer={agentWorking}
        refreshKey={sentCount}
        onSent={onSent}
        api={queueApi}
        onCount={setQueuedCount}
      />
    ) : null;
  const box = canMessage ? (
    <AgentMessageBox
      // One draft (and its pictures) per task: another task's never goes to this one, and a newer run on
      // this task keeps what's being written.
      key={taskId}
      taskId={taskId}
      runId={run.id}
      runNumber={run.number}
      project={projectSlug}
      canSteer={agentWorking}
      queueBlocked={queueBlockedReason(run)}
      readOnly={readOnly}
      onSent={(outcome) => {
        setSentCount((n) => n + 1);
        // SF-T-266: once it shows in the thread, the message sits near the top with the answer under it.
        if (fill) anchor();
        onSent?.(outcome);
      }}
      // SF-T-266: Stop a live run's turn (another run's would fail: Cancel it instead).
      onStop={run.live && active ? () => stopTurn(run.id, run.number) : null}
      send={send}
    />
  ) : (
    <p className="text-xs text-muted-foreground">Members can write to the agent here.</p>
  );
  return (
    <section ref={section} className={cn(fill ? 'flex h-full min-h-0 flex-col gap-3' : 'space-y-3')} aria-label="Agent">
      {runLine ? <AgentRunLine run={run} projectSlug={projectSlug} threadWorking={!!turnNow} className="text-xs" /> : null}
      <div className={cn(fill && 'relative min-h-0 flex-1')}>
        <div ref={stream} className={cn(fill && 'h-full overflow-y-auto pr-1')}>
          <div ref={content}>
            <AgentActivityFeed
              key={run.id}
              runId={run.id}
              project={projectSlug}
              // A parked live run's thread doesn't move until someone writes to it (the runs' poll sees it start again).
              live={active && !run.live_parked_at}
              layout="thread"
              preview={preview}
              readDiff={readDiff}
              summarize={canMessage ? () => summarize(run.id, { project: projectSlug }) : undefined}
              followThread={active}
              workingShownOutside={pill}
              onWorking={(at) => {
                setTurnSeen(at && runId ? { runId, at } : null);
                onThreadWorking?.(at && runId ? runId : null);
              }}
              onRead={(code) => setReadError((was) => (was?.runId === run.id && was.code === code ? was : { runId: run.id, code }))}
              // SF-T-273: a thread stuck on its missing worktree folder offers Continue in a new worktree under its error.
              onFolderGone={(gone) => setGoneFor(gone ? run.id : null)}
              renderFolderGone={(gone) => (
                <RunFolderGoneCard
                  gone={gone}
                  run={run}
                  projectSlug={projectSlug}
                  canContinue={canMessage}
                  restore={restore}
                  detail={restoreDetail}
                  onStarted={(outcome) => {
                    toast.success(`${messageOutcomeText(outcome)}: it makes the worktree again first`);
                    onSent?.(outcome);
                  }}
                />
              )}
              // The changed files, Summarize and Collapse all stay in reach as the thread scrolls under them: at the
              // top of the side pane's scrolling box (its tint over the page's background), else under whatever
              // the page pins above (the phone's task bar sets --task-sticky-top).
              headerClassName={cn(
                'sticky z-10 py-1.5',
                fill ? 'top-0 bg-background bg-linear-to-b from-muted/30 to-muted/30' : 'top-(--task-sticky-top,0px) bg-background'
              )}
            />
          </div>
          {/* SF-T-266: room under a just-sent message, so it can sit near the top while the answer comes. */}
          <div ref={spacer} aria-hidden className="h-0" />
        </div>
        {pill && turnNow ? (
          <WorkingPill
            since={turnNow.since}
            agents={turnNow.agents}
            queued={queuedCount}
            queuedPanel={
              queueProps ? (
                <QueuedMessages
                  runId={run.id}
                  project={projectSlug}
                  polling={!run.live_parked_at || agentWorking}
                  canSteer={agentWorking}
                  refreshKey={sentCount}
                  onSent={onSent}
                  api={queueApi}
                  onCount={setQueuedCount}
                  variant="rows"
                />
              ) : null
            } offline={readError?.runId === run.id && readError.code === 'MACHINE_OFFLINE' ? (run.machine?.name ?? 'The machine') : null} onJump={following ? null : follow} className="bottom-2" />
        ) : fill && !following ? (
          <JumpToLatestButton className="bottom-2" onClick={follow} />
        ) : null}
      </div>
      {pinned ? (
        // Phones: pinned to the foot of the screen as the task scrolls, as the Comments tab's box is (TaskDetailView):
        // -bottom-4 reaches past the pop-up's padding, and while you type in the task's other boxes it stays put.
        <div
          data-task-chat-box
          className="sticky -bottom-4 z-10 border-t bg-background pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-3 group-has-[:is(textarea,input):focus:not([data-task-chat-box]_*)]/task:relative"
        >
          {away ? <JumpToLatestButton className="bottom-full mb-2" onClick={jump} /> : null}
          {queued}
          {box}
        </div>
      ) : (
        <>
          {queued}
          {box}
        </>
      )}
    </section>
  );
}

/**
 * SF-T-266: the run the Agent tab shows, on one line: R-12, its status, Live, the machine. The full-screen chat's
 * title, so the run isn't named twice; the machine gives way first on a narrow screen.
 */
export function AgentRunLine({
  run,
  projectSlug,
  threadWorking = false,
  className,
}: {
  run: RunSummary;
  projectSlug: string;
  /** SF-T-266: its thread shows a turn going (the feed's read): a parked live run then shows Working, not waiting for you. */
  threadWorking?: boolean;
  className?: string;
}) {
  const active = isRunActive(run.status);
  // Someone wrote to the thread in T3 Code: it works though the run still waits (until the run follows that turn).
  const outsideTurn = threadWorking && active && run.status === 'waiting' && !!run.live_parked_at;
  const working = (run.status === 'running' && !run.live_parked_at) || outsideTurn;
  return (
    <div className={cn('flex min-w-0 items-center gap-2', className)}>
      <Link to={runPath(projectSlug, run.number)} className="shrink-0 font-medium hover:underline">
        {runRef(run.number)}
      </Link>
      <StatusBadge meta={outsideTurn ? RUN_THREAD_WORKING_META : runStatusMeta(run)} size="sm" dot={working} />
      {run.live && (!run.live_parked_at || outsideTurn) && active ? <StatusBadge meta={liveRunMeta(run)} size="sm" shape="square" /> : null}
      {run.machine ? <span className="min-w-0 truncate font-normal text-muted-foreground">on {run.machine.name}</span> : null}
      {run.wrap_up_at && active ? <span className="shrink-0 font-normal text-muted-foreground">· wrapping up</span> : null}
    </div>
  );
}
